// Side-effect module, imported first by the Inertia entrypoint so it runs
// before any dependency can reach for what it patches.
//
// `Object.hasOwn` (ES2022 — Chrome 93, Safari 15.4) is the one gap that
// actually bites us, and it bites on a path we can't otherwise reach.
// Inertia stores the page in history via `clonePageProps`, which tries
// `structuredClone(page.props)` and, on failure, falls back to es-toolkit's
// `cloneDeep`. `structuredClone` landed in almost exactly the same browsers
// (Chrome 98, Safari 15.4), so anything old enough to take that fallback is
// old enough to have no `Object.hasOwn` — and es-toolkit calls it while
// cloning arrays. The result was an uncaught
// `TypeError: Object.hasOwn is not a function` reported from real traffic:
// the history write rejects and the visitor's page transitions stop working.
//
// `allow_browser versions: :modern` (ApplicationController) doesn't cover
// this: it only turns away browsers it *recognises* as old, so an
// unrecognised agent — a locked-down practice PC, a preview/screenshot bot —
// runs the app on an engine our Vite build (no explicit `build.target`, so
// roughly Chrome 87 / Safari 14) still targets.
// Declared rather than used directly: the project compiles against ES2020
// (tsconfig.app.json), which is the whole reason this file exists.
const objectConstructor = Object as ObjectConstructor & {
  hasOwn?: (target: unknown, key: PropertyKey) => boolean
}

if (typeof objectConstructor.hasOwn !== 'function') {
  Object.defineProperty(Object, 'hasOwn', {
    value: function hasOwn(target: unknown, key: PropertyKey): boolean {
      if (target === null || target === undefined) {
        throw new TypeError('Cannot convert undefined or null to object')
      }
      return Object.prototype.hasOwnProperty.call(Object(target), key)
    },
    configurable: true,
    writable: true,
  })
}
